import React from 'react'
import { XIcon } from 'lucide-react'

import { composeRefs } from '@/shared/lib/composition'
import { cn } from '@/shared/lib/utils'

export enum Delimiter {
	Comma = ',',
	Enter = 'Enter',
}

export type TagInputProps = {
	id?: string
	invalid?: boolean
	placeholder?: string
	value: string[]
	onChange: (value: string[]) => void
	disabled?: boolean
	delimiter?: Delimiter
	onInputChange?: (value: string) => void
	onFocus?: React.FocusEventHandler<HTMLInputElement>
	onBlur?: React.FocusEventHandler<HTMLInputElement>
	addOnPaste?: boolean
	ref?: React.Ref<HTMLInputElement>
}

const TagInput = (props: TagInputProps) => {
	const {
		invalid,
		placeholder,
		value,
		onChange,
		delimiter = Delimiter.Comma,
		onInputChange,
		onFocus,
		onBlur,
		disabled = false,
		addOnPaste = false,
		ref,
	} = props

	const [inputValue, setInputValue] = React.useState('')
	const inputRef = React.useRef<HTMLInputElement>(null)

	const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const newValue = e.target.value
		if (addOnPaste && newValue.includes(delimiter)) {
			const splitValues = newValue
				.split(delimiter)
				.map((v) => v.trim())
				.filter((v) => v)
			splitValues.forEach((value) => {
				if (!value) return // Skip empty strings from split
				const newTagText = value.trim()
				onChange(uniq([...props.value, newTagText]))
			})
			setInputValue('')
		} else {
			setInputValue(newValue)
		}
		onInputChange?.(newValue)
	}

	const handleInputFocus = (event: React.FocusEvent<HTMLInputElement>) => {
		onFocus?.(event)
	}

	const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
		onBlur?.(event)
	}

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === delimiter || e.key === Delimiter.Enter) {
			e.preventDefault()
			const newTagText = inputValue.trim()
			if (newTagText !== '') {
				onChange(uniq([...props.value, newTagText]))
				setInputValue('')
			}
		}
	}

	return (
		<ComboboxChips aria-invalid={invalid}>
			{value?.map((tag) => (
				<ComboboxChip key={tag}>
					{tag}
					<button
						type='button'
						aria-label={`Remove ${tag}`}
						className='inline-flex cursor-pointer items-center justify-center border-none bg-transparent p-1 text-foreground opacity-50 hover:opacity-100'
						onClick={() => onChange(value.filter((t) => t !== tag))}
					>
						<XIcon className='size-3' />
					</button>
				</ComboboxChip>
			))}
			<ComboboxChipsInput
				id={props.id}
				ref={composeRefs(inputRef, ref)}
				type='text'
				placeholder={placeholder}
				value={inputValue}
				onChange={handleInputChange}
				onKeyDown={handleKeyDown}
				onFocus={handleInputFocus}
				onBlur={handleInputBlur}
				disabled={disabled}
			/>
		</ComboboxChips>
	)
}
function ComboboxChipsInput({
	className,
	...props
}: React.ComponentProps<'input'>) {
	return (
		<input
			data-slot='combobox-chip-input'
			className={cn('min-w-16 flex-1 outline-none', className)}
			{...props}
		/>
	)
}

TagInput.displayName = 'TagInput'
function ComboboxChips({
	className,
	...props
}: React.ComponentPropsWithRef<'div'>) {
	return (
		<div
			data-slot='combobox-chips'
			className={cn(
				'aria-invalid:border-destructive aria-invalid:ring-[3px] aria-invalid:ring-destructive/20',
				'flex min-h-8 flex-wrap items-center gap-1 rounded-lg border border-input bg-input/20 px-2.5 py-1 text-sm transition-colors focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50 has-aria-invalid:border-destructive has-aria-invalid:ring-[3px] has-aria-invalid:ring-destructive/20 has-data-[slot=combobox-chip]:px-1 dark:bg-input/30 dark:has-aria-invalid:border-destructive/50 dark:has-aria-invalid:ring-destructive/40',
				className
			)}
			{...props}
		/>
	)
}
function ComboboxChip({
	className,
	children,
	showRemove = true,
	...props
}: React.ComponentProps<'div'> & {
	showRemove?: boolean
}) {
	return (
		<div
			data-slot='combobox-chip'
			className={cn(
				'flex h-[calc(--spacing(5.25))] w-fit items-center justify-center gap-1 rounded-sm bg-tertiary/70 px-1.5 text-xs font-medium whitespace-nowrap text-foreground has-disabled:pointer-events-none has-disabled:cursor-not-allowed has-disabled:opacity-50 has-data-[slot=combobox-chip-remove]:pr-0 dark:bg-background',
				className
			)}
			{...props}
		>
			{children}
		</div>
	)
}
const uniq = (arr: string[]) => [...new Set(arr)]
export { TagInput }
